/**
 * ============================================================================
 *  progression.ts — Ce que le visiteur a vu, et où il s'est arrêté
 * ----------------------------------------------------------------------------
 *  Un guide, une clé : `ssixt.guide.<id>.v1` (`mariage`, `seminaire`), dans le
 *  `localStorage` du navigateur — jamais sur un serveur. Elle porte
 *
 *    watched : les chapitres vus (identifiants des étapes de la timeline). Un chapitre
 *              passe à « vu » quand son écran de fin est atteint ;
 *    last    : où reprendre — chapitre, rang de la scène, instant dans la voix.
 *
 *  Au retour du visiteur la visite reprend à `last`, sans ouvrir le menu.
 *
 *  Le stockage est INJECTÉ (`Stockage`) : les tests en donnent un factice, et
 *  l'absence de stockage (navigation privée, quota, cookies bloqués) n'est pas une
 *  erreur — la progression vit alors en mémoire, le temps de la visite. Rien ici ne
 *  lève jamais : une valeur illisible vaut « rien d'enregistré ».
 * ==========================================================================*/

export interface Dernier {
  chapterId: string;
  sceneIndex: number;
  /** Secondes dans la voix du chapitre. */
  t: number;
}

export interface Progression {
  watched: string[];
  last: Dernier | null;
}

/** Le plus petit morceau de `Storage` dont on a besoin. */
export interface Stockage {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
}

export const progressionVide = (): Progression => ({ watched: [], last: null });

export const cleStockage = (guideId: string): string => `ssixt.guide.${guideId}.v1`;

const estTexte = (valeur: unknown): valeur is string => typeof valeur === "string" && valeur !== "";
const estNombre = (valeur: unknown): valeur is number => typeof valeur === "number" && Number.isFinite(valeur);

/** Relit une progression ; tout ce qui n'est pas au bon format est ignoré, pas corrigé. */
export function lireProgression(stockage: Stockage | null, guideId: string): Progression {
  if (!stockage) return progressionVide();
  try {
    const brut = stockage.getItem(cleStockage(guideId));
    if (!brut) return progressionVide();
    const donnee = JSON.parse(brut) as { watched?: unknown; last?: unknown };
    const watched = Array.isArray(donnee.watched) ? [...new Set(donnee.watched.filter(estTexte))] : [];
    const dernier = donnee.last as Partial<Dernier> | null | undefined;
    const last =
      dernier && estTexte(dernier.chapterId) && estNombre(dernier.sceneIndex) && estNombre(dernier.t)
        ? { chapterId: dernier.chapterId, sceneIndex: Math.max(0, Math.trunc(dernier.sceneIndex)), t: Math.max(0, dernier.t) }
        : null;
    return { watched, last };
  } catch {
    return progressionVide();
  }
}

/** Écrit la progression. Retourne faux si le stockage n'a pas pu la recevoir. */
export function ecrireProgression(stockage: Stockage | null, guideId: string, progression: Progression): boolean {
  if (!stockage) return false;
  try {
    stockage.setItem(cleStockage(guideId), JSON.stringify(progression));
    return true;
  } catch {
    return false;
  }
}

export function marquerVu(progression: Progression, chapterId: string): Progression {
  if (progression.watched.includes(chapterId)) return progression;
  return { ...progression, watched: [...progression.watched, chapterId] };
}

export const noterDernier = (progression: Progression, last: Dernier): Progression => ({ ...progression, last });

/* --- reprise --------------------------------------------------------------- */

export interface ChapitreRepris {
  id: string;
  duree: number;
  scenes: number;
}

export interface PointDeReprise {
  index: number;
  t: number;
  sceneIndex: number;
}

/** Marge sous laquelle « presque à la fin » vaut « fini » : on repart du chapitre suivant. */
const MARGE_FIN_S = 1;

/**
 * Où reprendre. `null` : rien à reprendre — pas d'enregistrement, chapitre disparu du
 * guide, ou dernier chapitre déjà fini (on repart du début). Un chapitre dont la fin a été
 * atteinte reprend au début du suivant : le visiteur a déjà vu celui-là.
 */
export function pointDeReprise(last: Dernier | null, chapitres: readonly ChapitreRepris[]): PointDeReprise | null {
  if (!last) return null;
  const index = chapitres.findIndex((chapitre) => chapitre.id === last.chapterId);
  const chapitre = chapitres[index];
  if (!chapitre) return null;
  const fini = chapitre.duree > 0 && last.t >= chapitre.duree - MARGE_FIN_S;
  if (fini) return index + 1 < chapitres.length ? { index: index + 1, t: 0, sceneIndex: 0 } : null;
  return { index, t: chapitre.duree > 0 ? Math.min(last.t, chapitre.duree) : last.t, sceneIndex: last.sceneIndex };
}

/**
 * Sous ce seuil dans le PREMIER chapitre, le visiteur n’a pas vraiment commencé : quelques secondes
 * de voix avant de refermer l’onglet ne méritent pas de question au retour.
 */
const SEUIL_COMMENCE_S = 5;

/**
 * Faut-il demander « reprendre ou recommencer » ? Oui dès qu’il y a quelque chose à reprendre qui
 * ne soit pas le tout début : un chapitre plus loin, ou plus de quelques secondes dans le premier.
 */
export function repriseProposable(reprise: PointDeReprise | null): reprise is PointDeReprise {
  return reprise !== null && (reprise.index > 0 || reprise.t >= SEUIL_COMMENCE_S);
}
